'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';

const navLinks = [
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Features', href: '#features' },
  { label: 'Testimonials', href: '#testimonials' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, loading, signOut, isAdmin } = useAuth();
  const router = useRouter();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSignOut = async () => {
    await signOut();
    router.push('/homepage');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-xl shadow-[0_1px_0_rgba(15,23,42,0.06)]'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-[1280px] mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-2.5 group">
          <AppLogo
            size={36}
            iconName="SparklesIcon"
            className="transition-transform group-hover:scale-110"
          />
          <span
            className={`font-display text-xl font-800 tracking-tight transition-colors ${
              scrolled ? 'text-secondary' : 'text-white'
            }`}
          >
            CampusWash
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => handleNavClick(link.href)}
              className={`text-sm font-600 transition-colors cursor-pointer ${
                scrolled
                  ? 'text-muted hover:text-secondary' : 'text-white/80 hover:text-white'
              }`}
            >
              {link.label}
            </button>
          ))}
        </div>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-4">
          {!loading && user ? (
            <>
              <Link
                href={isAdmin() ? '/admin' : '/dashboard'}
                className={`text-sm font-600 transition-colors ${
                  scrolled ? 'text-secondary hover:text-primary' : 'text-white/80 hover:text-white'
                }`}
              >
                {isAdmin() ? 'Admin Portal' : 'My Orders'}
              </Link>
              <button
                onClick={handleSignOut}
                className="bg-primary text-white px-6 py-2.5 rounded-full text-sm font-700 hover:bg-primary-dark transition-all hover:scale-105 cursor-pointer"
              >
                Sign Out
              </button>
            </>
          ) : (
            <>
              <Link
                href="/login"
                className={`text-sm font-600 transition-colors cursor-pointer ${
                  scrolled ? 'text-secondary' : 'text-white/80 hover:text-white'
                }`}
              >
                Sign In
              </Link>
              <Link
                href="/register"
                className="bg-primary text-white px-6 py-2.5 rounded-full text-sm font-700 hover:bg-primary-dark transition-all hover:scale-105 cta-pulse cursor-pointer"
              >
                Get Started
              </Link>
            </>
          )}
        </div>

        {/* Hamburger */}
        <button
          className={`md:hidden p-2 rounded-lg transition-colors cursor-pointer ${
            scrolled ? 'text-secondary' : 'text-white'
          }`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            {menuOpen ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </>
            ) : (
              <>
                <line x1="3" y1="7" x2="21" y2="7" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="17" x2="21" y2="17" />
              </>
            )}
          </svg>
        </button>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 bg-white border-t border-border ${
          menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 py-6 space-y-4">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => handleNavClick(link.href)}
              className="block w-full text-left text-base font-600 text-secondary hover:text-primary transition-colors py-2 cursor-pointer"
            >
              {link.label}
            </button>
          ))}
          <div className="pt-4 space-y-3 border-t border-border">
            {!loading && user ? (
              <>
                <Link
                  href={isAdmin() ? '/admin' : '/dashboard'}
                  className="block w-full text-center text-sm font-600 text-secondary py-2"
                  onClick={() => setMenuOpen(false)}
                >
                  {isAdmin() ? 'Admin Portal' : 'My Orders'}
                </Link>
                <button
                  onClick={() => { setMenuOpen(false); handleSignOut(); }}
                  className="w-full bg-primary text-white py-3 rounded-full text-sm font-700 hover:bg-primary-dark transition-colors cursor-pointer"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  href="/login"
                  className="block w-full text-center text-sm font-600 text-muted py-2"
                  onClick={() => setMenuOpen(false)}
                >
                  Sign In
                </Link>
                <Link
                  href="/register"
                  className="block w-full bg-primary text-white py-3 rounded-full text-sm font-700 hover:bg-primary-dark transition-colors text-center"
                  onClick={() => setMenuOpen(false)}
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}